import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import splitArrayRole from "./utils/splitArrayRole";
import { SearchHit } from "../../../../creditSearch";
import { splitGutter } from "./gutterUtils";

const setSplitAlignment = (text_alignment) => {
  switch (text_alignment) {
    case "left":
      return [" text-left ", " text-left "];
    case "justify":
      return [" text-left ", " text-right "];
    case "right":
      return [" text-right ", " text-right "];
    case "gutter":
      return [" text-right ", " text-left "];
    default:
      return [" text-right ", " text-left "];
  }
};

const getAlignment = (text_alignment, columns) => {
  switch (text_alignment) {
    case "left":
      return Array(columns).fill(" text-start ");
    case "justify":
      if (columns === 2) return [" text-start ", " text-end "];
      if (columns === 3) return [" text-start ", " text-center ", " text-end "];
      return [" text-start ", " text-end "];
    case "right":
      return Array(columns).fill(" text-end ");
    case "center":
      return Array(columns).fill(" text-center ");
    default:
      return Array(columns).fill(" text-end ");
  }
};

const getPadding = (textAlignment, noOfCols, colIdx, gutter) => {
  let pl = 0;
  let pr = 0;
  if (textAlignment === "gutter") {
    if (noOfCols === 2) {
      if (colIdx === 0) pr = gutter;
      if (colIdx === 1) pl = gutter;
    }
    if (noOfCols === 4) {
      if (colIdx === 1) pr = gutter;
      if (colIdx === 2) pl = gutter;
    }
  }
  return {
    paddingLeft: `${pl}px`,
    paddingRight: `${pr}px`,
  };
};

const roleText = (item) =>
  item?.role == null ? "" : String(item.role).trim();

const nameText = (item) =>
  item?.name == null ? "" : String(item.name).trim();

const PROBE_FONT_PROPS = [
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "fontStretch",
  "fontVariant",
  "fontKerning",
  "fontFeatureSettings",
  "letterSpacing",
  "wordSpacing",
  "lineHeight",
  "textTransform",
];

// Line breaks inside a cell, from the browser's own wrap.
function readWrapLines(el) {
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  const lines = [];
  let current = "";
  let lastTop = null;

  while (walker.nextNode()) {
    const node = walker.currentNode;
    const value = node.nodeValue || "";
    for (let i = 0; i < value.length; i += 1) {
      range.setStart(node, i);
      range.setEnd(node, i + 1);
      const rect = range.getClientRects()[0];
      if (!rect || (rect.width === 0 && rect.height === 0)) {
        current += value[i];
        continue;
      }
      if (
        lastTop !== null &&
        Math.abs(rect.top - lastTop) > Math.max(2, rect.height * 0.35)
      ) {
        lines.push(current);
        current = value[i];
      } else {
        current += value[i];
      }
      lastTop = rect.top;
    }
  }

  if (current !== "" || lines.length === 0) lines.push(current);
  return lines;
}

function visibleLines(lines) {
  const cleaned = (lines || [])
    .map((line) => String(line).replace(/\s+/g, " ").trim())
    .filter(Boolean);
  return cleaned.length ? cleaned : [""];
}

// Most credit rows never wrap. Detect that common case with one Range read
// instead of building hidden probes and measuring every character.
function hasMultipleVisualLines(el) {
  if (!el || el.clientWidth < 2) return false;
  const sample = el.querySelector("span") || el;
  if (!sample.textContent?.trim()) return false;

  const range = document.createRange();
  range.selectNodeContents(sample);
  const lineTops = [];
  Array.from(range.getClientRects()).forEach((rect) => {
    if (!rect || (rect.width === 0 && rect.height === 0)) return;
    if (
      !lineTops.some(
        (top) => Math.abs(top - rect.top) <= Math.max(1, rect.height * 0.2),
      )
    ) {
      lineTops.push(rect.top);
    }
  });
  range.detach?.();
  return lineTops.length > 1;
}

function measureWrappedLines(text, td) {
  const source = text == null ? "" : String(text);
  if (!source.trim() || !td || td.clientWidth < 2) return [source];

  const sample = td.querySelector("span") || td;
  const cs = window.getComputedStyle(sample);
  const tdCs = window.getComputedStyle(td);
  const width = Math.max(
    0,
    td.clientWidth -
      (parseFloat(tdCs.paddingLeft) || 0) -
      (parseFloat(tdCs.paddingRight) || 0) -
      (parseFloat(cs.paddingLeft) || 0) -
      (parseFloat(cs.paddingRight) || 0),
  );
  if (width < 2) return [source];

  const probe = document.createElement("div");
  probe.setAttribute("data-html2canvas-ignore", "true");
  probe.style.position = "absolute";
  probe.style.left = "-9999px";
  probe.style.top = "0";
  probe.style.visibility = "hidden";
  probe.style.boxSizing = "content-box";
  probe.style.width = `${width}px`;
  probe.style.margin = "0";
  probe.style.padding = "0";
  probe.style.border = "0";
  probe.style.whiteSpace = "normal";
  probe.style.wordBreak = "keep-all";
  probe.style.overflowWrap = "normal";
  probe.style.wordWrap = "normal";
  probe.style.hyphens = "none";
  PROBE_FONT_PROPS.forEach((prop) => {
    probe.style[prop] = cs[prop];
  });
  probe.textContent = source;

  let lines = [source];
  try {
    document.body.appendChild(probe);
    lines = visibleLines(readWrapLines(probe));
  } finally {
    probe.remove();
  }
  // One line keeps the original string, including a role placeholder.
  if (lines.length <= 1) return [source];
  return lines;
}

// Within a wrapped pair, a shorter role stays on the first line while a
// shorter name stays on the last line. This keeps name wrapping downward and
// role wrapping upward, including when the user selects Names First.
export function alignWrappedLines(
  leftLines,
  rightLines,
  leftIsRole = true,
) {
  const left = leftLines?.length ? leftLines : [""];
  const right = rightLines?.length ? rightLines : [""];
  if (left.length <= 1 && right.length <= 1) return null;
  const count = Math.max(left.length, right.length);
  const pad = (lines, isRole) => {
    const blanks = Array(count - lines.length).fill("");
    return isRole ? [...lines, ...blanks] : [...blanks, ...lines];
  };
  return {
    left: pad(left, leftIsRole),
    right: pad(right, !leftIsRole),
  };
}

function sameLineSet(a, b) {
  if (a == null || b == null) return a == null && b == null;
  if (a.left.length !== b.left.length || a.right.length !== b.right.length) return false;
  for (let i = 0; i < a.left.length; i += 1) {
    if (a.left[i] !== b.left[i] || a.right[i] !== b.right[i]) return false;
  }
  return true;
}

export function shouldResetWrappedLinesForResize(
  previousWidth,
  nextWidth,
  hasWrappedLines,
) {
  return (
    Boolean(hasWrappedLines) &&
    Number(previousWidth) > 0 &&
    Number(nextWidth) > Number(previousWidth) + 1
  );
}

export function getRoleNameResizeBox(node) {
  return (
    node?.closest(".roleName-ragged-group") ||
    node?.closest(".roleName-gutter-column") ||
    node?.closest(".roleName-optical-grid") ||
    node?.closest("table") ||
    null
  );
}

// One role/name pair. When min width wraps one side, the other side stays on
// the first row so the two credits share their first line in preview/render.
function RoleNamePair({
  leftText,
  rightText,
  leftTdStyle,
  rightTdStyle,
  leftSpanStyle,
  rightSpanStyle,
  layout = "table",
  leftIsRole = true,
  roleNameGroupId,
}) {
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const roRef = useRef(null);
  const passes = useRef(0);
  const lastBox = useRef(0);
  const [rowLines, setRowLines] = useState(null);
  const rowLinesRef = useRef(null);

  const measureKey = [
    leftText,
    rightText,
    leftTdStyle?.fontSize,
    leftTdStyle?.fontFamily,
    leftTdStyle?.fontWeight,
    leftTdStyle?.lineHeight,
    leftSpanStyle?.fontFamily,
    leftSpanStyle?.fontSize,
    leftTdStyle?.paddingLeft,
    leftTdStyle?.paddingRight,
    rightTdStyle?.fontSize,
    rightTdStyle?.fontFamily,
    rightTdStyle?.fontWeight,
    rightTdStyle?.lineHeight,
    rightSpanStyle?.fontFamily,
    rightSpanStyle?.fontSize,
    rightTdStyle?.paddingLeft,
    rightTdStyle?.paddingRight,
    leftIsRole,
  ].join("|");

  const bindLeftRef = useCallback((node) => {
    if (leftRef.current && roRef.current) {
      roRef.current.unobserve(leftRef.current);
    }
    leftRef.current = node;
    if (node && roRef.current) roRef.current.observe(node);
  }, []);

  const bindRightRef = useCallback((node) => {
    if (rightRef.current && roRef.current) {
      roRef.current.unobserve(rightRef.current);
    }
    rightRef.current = node;
    if (node && roRef.current) roRef.current.observe(node);
  }, []);

  useLayoutEffect(() => {
    let remeasureFrame = null;

    const measure = () => {
      const leftTd = leftRef.current;
      const rightTd = rightRef.current;
      if (!leftTd || !rightTd) return;

      // Ragged tables and optically centered gutter grids are intrinsically
      // sized. Once a wrapped pair has been converted into explicit rows, the
      // inner layout can stay narrow even when its parent grows. Measure the
      // stable outer group/column so widening the block releases the saved
      // rows and lets the browser lay out the full strings again.
      const layoutBox = getRoleNameResizeBox(leftTd);
      const box = layoutBox?.clientWidth || 0;
      const previousBox = lastBox.current;
      if (box && Math.abs(box - lastBox.current) > 1) {
        passes.current = 0;
        lastBox.current = box;
      }

      if (
        shouldResetWrappedLinesForResize(
          previousBox,
          box,
          rowLinesRef.current,
        )
      ) {
        rowLinesRef.current = null;
        setRowLines(null);
        if (typeof requestAnimationFrame === "function") {
          remeasureFrame = requestAnimationFrame(measure);
        }
        return;
      }

      if (passes.current > 3) return;

      if (
        !rowLinesRef.current &&
        !hasMultipleVisualLines(leftTd) &&
        !hasMultipleVisualLines(rightTd)
      ) {
        return;
      }

      const next = alignWrappedLines(
        measureWrappedLines(leftText, leftTd),
        measureWrappedLines(rightText, rightTd),
        leftIsRole,
      );
      setRowLines((prev) => {
        if (sameLineSet(prev, next)) return prev;
        passes.current += 1;
        rowLinesRef.current = next;
        return next;
      });
    };

    if (typeof ResizeObserver !== "function") {
      measure();
      return undefined;
    }

    const ro = new ResizeObserver(measure);
    roRef.current = ro;
    if (leftRef.current) ro.observe(leftRef.current);
    if (rightRef.current) ro.observe(rightRef.current);
    const layoutBox = getRoleNameResizeBox(leftRef.current);
    if (layoutBox) ro.observe(layoutBox);
    measure();
    return () => {
      if (
        remeasureFrame != null &&
        typeof cancelAnimationFrame === "function"
      ) {
        cancelAnimationFrame(remeasureFrame);
      }
      ro.disconnect();
      roRef.current = null;
    };
  }, [leftIsRole, measureKey, leftText, rightText]);

  const leftLines = rowLines ? rowLines.left : [leftText];
  const rightLines = rowLines ? rowLines.right : [rightText];
  const splitSpan = rowLines ? { whiteSpace: "nowrap" } : null;

  const leftCellStyle = splitSpan
    ? { ...leftSpanStyle, ...splitSpan }
    : leftSpanStyle;
  const rightCellStyle = splitSpan
    ? { ...rightSpanStyle, ...splitSpan }
    : rightSpanStyle;

  if (layout === "grid") {
    return leftLines.map((line, index) => (
      <React.Fragment key={index}>
        <div
          ref={index === 0 ? bindLeftRef : undefined}
          className="roleName_td FontSIzeScale"
          data-role-name-group-id={roleNameGroupId}
          style={leftTdStyle}
        >
          <span style={leftCellStyle}>
            {line ? <SearchHit text={line} /> : null}
          </span>
        </div>
        <div
          ref={index === 0 ? bindRightRef : undefined}
          className="roleName_td FontSIzeScale"
          data-role-name-group-id={roleNameGroupId}
          style={rightTdStyle}
        >
          <span style={rightCellStyle}>
            {rightLines[index] ? (
              <SearchHit text={rightLines[index]} />
            ) : null}
          </span>
        </div>
      </React.Fragment>
    ));
  }

  return leftLines.map((line, index) => (
    <tr key={index}>
      <td
        ref={index === 0 ? bindLeftRef : undefined}
        className="roleName_td FontSIzeScale"
        data-role-name-group-id={roleNameGroupId}
        style={leftTdStyle}
      >
        <span style={leftCellStyle}>
          {line ? <SearchHit text={line} /> : null}
        </span>
      </td>
      <td
        ref={index === 0 ? bindRightRef : undefined}
        className="roleName_td FontSIzeScale"
        data-role-name-group-id={roleNameGroupId}
        style={rightTdStyle}
      >
        <span style={rightCellStyle}>
          {rightLines[index] ? <SearchHit text={rightLines[index]} /> : null}
        </span>
      </td>
    </tr>
  ));
}

// A role row is any credit with a real role. Empty / "No Roll" is not a role.
const isRoleRow = (item) => {
  const role = roleText(item).toLowerCase();
  if (!role) return false;
  if (role === "no roll" || role === "." || role === ". .") return false;
  return true;
};

const isNameRow = (item) => Boolean(nameText(item));

// One group = one role + the name-only rows that follow it until the next role.
const groupByRole = (columnItems = []) => {
  const groups = [];
  for (const item of columnItems) {
    if (isRoleRow(item) || groups.length === 0) {
      groups.push([item]);
    } else {
      groups[groups.length - 1].push(item);
    }
  }
  return groups;
};

function RoleName2({ items, options, fontStyle, settings, isCard }) {
  // Drop rows that have NEITHER a role nor a name, so empty rows don't render as
  // blank gaps and don't count toward column balancing in the double-column
  // (gutter/left/right/justify) and ragged layouts.
  // const nonEmptyItems = (items || []).filter((item) => {
  //   const hasRole = item?.role != null && String(item.role).trim() !== "";
  //   const hasName = item?.name != null && String(item.name).trim() !== "";
  //   return hasRole || hasName;
  // });

  const visibleItems = useMemo(
    () =>
      (items || []).filter((item) => isRoleRow(item) || isNameRow(item)),
    [items],
  );

  const arraySplitDataByRole = useMemo(
    () =>
      options?.double_columns ? splitArrayRole(visibleItems, options).ans : [],
    [options, visibleItems],
  );

  // ==========================================================================================

  const arraySplitData = useMemo(
    () =>
      options?.double_columns ? [] : splitArrayRole(visibleItems, options).ans,
    [options, visibleItems],
  );
  const textAlignment = getAlignment(options?.text_alignment, options?.columns);

  const {
    lineHeight: _roleLineHeight,
    textDecoration: ___,
    marginBottom: _roleMb,
    ...roleStyle
  } = fontStyle.role;
  const {
    lineHeight: _nameLineHeight,
    textDecoration: ____,
    marginBottom: _nameMb,
    ...nameStyle
  } = fontStyle.name;

  const pairGapPx =
    Number(
      fontStyle?.name?.marginBottom ??
        fontStyle?.role?.marginBottom ??
        options?.row_gap ??
        0,
    ) || 0;

  const renderPairGroups = (
    columnItems,
    renderRow,
    namesFirst = false,
    groupTableStyle = null,
  ) =>
    groupByRole(columnItems).map((group, groupIdx) => {
      const roleItem = group.find(isRoleRow);
      const ordered = namesFirst
        ? [
            ...group.filter(isNameRow).map((item) => ({ ...item, role: null })),
            ...(roleItem ? [{ ...roleItem, name: null }] : []),
          ]
        : group;
      const rows = ordered.map(renderRow);
      return (
        <div
          key={groupIdx}
          data-role-name-group
          style={{ width: "100%" }}
        >
          {groupTableStyle ? (
            <table className="roleName_tr" style={groupTableStyle}>
              <tbody>{rows}</tbody>
            </table>
          ) : (
            rows
          )}
          {roleItem && pairGapPx > 0 ? (
            <div
              style={{
                height: `${pairGapPx}px`,
                minHeight: `${pairGapPx}px`,
                width: "100%",
                flexShrink: 0,
                overflow: "hidden",
                fontSize: 0,
                lineHeight: 0,
              }}
              aria-hidden
            />
          ) : null}
        </div>
      );
    });

  // Layout Line Height is a unitless point (1, 1.1, 1.2). Use it as CSS
  // line-height — not px, and not margin-bottom.
  const roleLh = Number(fontStyle?.role?.lineHeight) || 1;
  const nameLh = Number(fontStyle?.name?.lineHeight) || 1;

  const splitTextAlign = (() => {
    switch (options.text_alignment) {
      case "left":
        return ["left", "left"];
      case "right":
        return ["right", "right"];
      case "justify":
        return ["left", "right"];
      case "gutter":
      default:
        return ["right", "left"];
    }
  })();

  const stackedTextAlign =
    options.text_alignment === "left"
      ? "left"
      : options.text_alignment === "right"
      ? "right"
      : "center";

  const cellTypeStyle = {
    // Role and Name can use different fonts and sizes. Align their text
    // baselines in both project types so changing either size cannot make the
    // smaller side sit visibly lower. Card capture also normalizes to this
    // same baseline rule, keeping preview and rendered frames identical.
    verticalAlign: "baseline",
    fontKerning: "none",
    fontFeatureSettings: '"kern" 0',
    textRendering: "geometricPrecision",
    WebkitFontSmoothing: "antialiased",
    wordBreak: "keep-all",
    overflowWrap: "normal",
    wordWrap: "normal",
    hyphens: "none",
  };

  const tableGutterStyle = {
    width: "100%",
    tableLayout: "fixed",
    borderCollapse: "collapse",
    borderSpacing: 0,
    marginTop: 0,
    marginLeft: "auto",
    marginRight: "auto",
    marginBottom: 0,
    wordBreak: "keep-all",
    overflowWrap: "normal",
  };

  const tableRaggedStyle = {
    borderCollapse: "collapse",
    borderSpacing: 0,
    marginTop: 0,
    marginLeft: "auto",
    marginRight: "auto",
    marginBottom: 0,
    wordBreak: "keep-all",
    overflowWrap: "normal",
  };

  const roleTextBoxStyle = {
    display: isCard ? "block" : "inline-block",
    maxWidth: "100%",
    verticalAlign: "baseline",
    wordBreak: "keep-all",
    overflowWrap: "normal",
    wordWrap: "normal",
  };

  const nameTextBoxStyle = {
    display: isCard ? "block" : "inline-block",
    maxWidth: "100%",
    verticalAlign: "baseline",
    wordBreak: "keep-all",
    overflowWrap: "normal",
    wordWrap: "normal",
  };

  const firstCellTextBox = options.name_on_left
    ? nameTextBoxStyle
    : roleTextBoxStyle;
  const secondCellTextBox = options.name_on_left
    ? roleTextBoxStyle
    : nameTextBoxStyle;

  // Center/gutter only: equal share of the outer wrapper. Do not use this
  // for ragged — those tables size to content, not 50%/50% cells.
  const roleNameColCount = Math.max(1, Number(options?.columns) || 1);
  const useOpticallyCenteredTwoColumnLayout =
    roleNameColCount === 2 && options?.text_alignment === "gutter";
  const roleNameInnerColPct = `${100 / roleNameColCount}%`;
  const roleNameGutterColStyle = {
    width: roleNameInnerColPct,
    flex: `1 1 ${roleNameInnerColPct}`,
    maxWidth: roleNameInnerColPct,
    minWidth: 0,
    boxSizing: "border-box",
  };
  const roleNameRaggedColStyle = {
    width: `${options.column_width}%`,
    minWidth: 0,
    boxSizing: "border-box",
  };

  // The setting describes the complete gap between the role and name cells.
  // Split it across both cells so a 5px gutter renders as 5px, not 10px.
  const cellGutter = splitGutter(settings?.gutter);

  const tdStyleRight = {
    width: "50%",
    boxSizing: "border-box",
    textAlign: splitTextAlign[0],
    paddingRight: `${cellGutter}px`,
    paddingLeft: 0,
    ...(options.name_on_left ? nameStyle : roleStyle),
    ...cellTypeStyle,
    lineHeight: options.name_on_left ? nameLh : roleLh,
    whiteSpace: "normal",
  };

  const tdStyleLeft = {
    width: "50%",
    boxSizing: "border-box",
    textAlign: splitTextAlign[1],
    paddingLeft: `${cellGutter}px`,
    paddingRight: 0,
    ...(options.name_on_left ? roleStyle : nameStyle),
    ...cellTypeStyle,
    lineHeight: options.name_on_left ? roleLh : nameLh,
    whiteSpace: "normal",
  };

  const tdStyleRightColumn = {
    textAlign: "right",
    paddingRight: `${settings?.ragged_gutter / 2}px`,
    paddingLeft: "10px",
    ...(options.name_on_left ? nameStyle : roleStyle),
    ...cellTypeStyle,
    lineHeight: options.name_on_left ? nameLh : roleLh,
  };

  const tdStyleLeftColumn = {
    textAlign: "left",
    paddingLeft: `${settings.ragged_gutter / 2}px`,
    paddingRight: "10px",
    ...(options.name_on_left ? roleStyle : nameStyle),
    ...cellTypeStyle,
    lineHeight: options.name_on_left ? roleLh : nameLh,
  };

  const getRoleNamePairProps = (item, { optical = false } = {}) => ({
    leftIsRole: !options.name_on_left,
    leftText: options.name_on_left
      ? item.name == null
        ? ""
        : String(item.name)
      : item.role == null
      ? "No Roll"
      : String(item.role),
    rightText: options.name_on_left
      ? item.role == null
        ? "No Roll"
        : String(item.role)
      : item.name == null
      ? ""
      : String(item.name),
    leftTdStyle: optical
      ? {
          ...tdStyleRight,
          width: "auto",
          minWidth: 0,
          paddingLeft: 0,
          paddingRight: 0,
        }
      : tdStyleRight,
    rightTdStyle: optical
      ? {
          ...tdStyleLeft,
          width: "auto",
          minWidth: 0,
          paddingLeft: 0,
          paddingRight: 0,
        }
      : tdStyleLeft,
    leftSpanStyle: {
      ...firstCellTextBox,
      textDecoration: options.name_on_left
        ? fontStyle.name.textDecoration
        : item.role == null
        ? "none"
        : fontStyle.role.textDecoration,
      color: options.name_on_left
        ? ""
        : item.role == null
        ? "transparent"
        : fontStyle.role.color,
      fontFamily: options.name_on_left
        ? fontStyle.name.fontFamily
        : fontStyle.role.fontFamily,
    },
    rightSpanStyle: {
      ...secondCellTextBox,
      textDecoration: options.name_on_left
        ? item.role == null
          ? "none"
          : fontStyle.role.textDecoration
        : fontStyle.name.textDecoration,
      color: options.name_on_left
        ? item.role == null
          ? "transparent"
          : fontStyle.role.color
        : "",
      fontFamily: options.name_on_left
        ? fontStyle.role.fontFamily
        : fontStyle.name.fontFamily,
    },
  });

  const getRaggedRoleNamePairProps = (item) => ({
    leftIsRole: !options.name_on_left,
    leftText: options.name_on_left
      ? item.name == null
        ? ""
        : String(item.name)
      : item.role == null
      ? ". "
      : String(item.role),
    rightText: options.name_on_left
      ? item.role == null
        ? " ."
        : String(item.role)
      : item.name == null
      ? ""
      : String(item.name),
    leftTdStyle: tdStyleRightColumn,
    rightTdStyle: tdStyleLeftColumn,
    leftSpanStyle: {
      ...firstCellTextBox,
      textDecoration: options.name_on_left
        ? fontStyle.name.textDecoration
        : item.role == null
        ? "none"
        : fontStyle.role.textDecoration,
      color: options.name_on_left
        ? ""
        : item.role == null
        ? "transparent"
        : fontStyle.role.color,
    },
    rightSpanStyle: {
      ...secondCellTextBox,
      textDecoration: options.name_on_left
        ? item.role == null
          ? "none"
          : fontStyle.role.textDecoration
        : fontStyle.name.textDecoration,
      color: options.name_on_left
        ? item.role == null
          ? "transparent"
          : fontStyle.role.color
        : "",
    },
  });

  const renderRaggedExtraName = (item, key) => (
    <div
      key={key}
      className="roleName-ragged-extra-name FontSIzeScale"
      style={{
        ...nameStyle,
        ...cellTypeStyle,
        width: "100%",
        paddingLeft: 0,
        paddingRight: 0,
        lineHeight: nameLh,
        textAlign: "center",
        whiteSpace: "normal",
      }}
    >
      <span
        style={{
          ...nameTextBoxStyle,
          fontFamily: fontStyle.name.fontFamily,
          textDecoration: fontStyle.name.textDecoration,
        }}
      >
        <SearchHit text={nameText(item)} />
      </span>
    </div>
  );

  // The first role/name pair responds to the ragged gutter. Additional names
  // have no role-side partner, so keep them centered in the column without
  // inheriting that gutter's invisible role cell or horizontal padding.
  const renderRaggedGroups = (columnItems) =>
    groupByRole(columnItems).map((group, groupIdx) => {
      const roleItem = group.find(isRoleRow);

      return (
        <div
          key={groupIdx}
          className="roleName-ragged-group"
          data-role-name-group
          style={{ width: "100%" }}
        >
          {group.map((item, itemIdx) =>
            isRoleRow(item) ? (
              <table
                key={itemIdx}
                className="roleName_tr roleName-ragged-pair"
                style={tableRaggedStyle}
              >
                <tbody>
                  <RoleNamePair {...getRaggedRoleNamePairProps(item)} />
                </tbody>
              </table>
            ) : (
              renderRaggedExtraName(item, itemIdx)
            ),
          )}
          {roleItem && pairGapPx > 0 ? (
            <div
              style={{
                height: `${pairGapPx}px`,
                minHeight: `${pairGapPx}px`,
                width: "100%",
                flexShrink: 0,
                overflow: "hidden",
                fontSize: 0,
                lineHeight: 0,
              }}
              aria-hidden
            />
          ) : null}
        </div>
      );
    });

  // Render uses special table normalization for legacy credit rows. This grid
  // is intentionally table-free so its intrinsic centering remains identical
  // in the editor preview and in exported frames.
  const renderOpticallyCenteredGroups = (columnItems, columnIndex) => {
    const groups = groupByRole(columnItems);

    return (
      <div
        className="roleName-optical-grid"
        style={{
          display: "grid",
          gridTemplateColumns:
            "minmax(0, max-content) minmax(0, max-content)",
          columnGap: `${Number(settings?.gutter) || 0}px`,
          alignItems: "baseline",
          alignSelf: "center",
          maxWidth: "100%",
        }}
      >
        {groups.map((group, groupIdx) => {
          const roleItem = group.find(isRoleRow);

          return (
            <React.Fragment key={groupIdx}>
              {group.map((item, itemIdx) => (
                <RoleNamePair
                  key={itemIdx}
                  {...getRoleNamePairProps(item, { optical: true })}
                  layout="grid"
                  roleNameGroupId={`optical-${columnIndex}-${groupIdx}`}
                />
              ))}
              {roleItem && pairGapPx > 0 ? (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    height: `${pairGapPx}px`,
                    minHeight: `${pairGapPx}px`,
                    width: "100%",
                    overflow: "hidden",
                    fontSize: 0,
                    lineHeight: 0,
                  }}
                  aria-hidden
                />
              ) : null}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return options.double_columns ? (
    options.text_alignment === "ragged" ? (
      // Double Column (Ragged Align)

      <div
        className={`flex flex-row flex-wrap ${
          options?.columns === 1 ? "justify-center" : ""
        } 
        ${options?.text_alignment === "gutter" ? " justify-center " : ""}`}
        style={{
          width: `${options.column_width * options.columns}%`,
          margin: "0 auto",
          wordBreak: "keep-all",
          overflowWrap: "normal",
        }}
      >
        {arraySplitDataByRole?.map((items, index) => (
          <div
            key={index}
            className={`flex flex-col flex-1 float-left`}
            style={roleNameRaggedColStyle}
          >
            {renderRaggedGroups(items)}
          </div>
        ))}
      </div>
    ) : (
      // Double Column (Gutter Align/Left/Right/Justify Align)

      <div
        className={`flex flex-row flex-wrap ${
          options?.columns === 1 ? "justify-center" : ""
        } 
        ${options?.text_alignment === "gutter" ? " justify-center " : ""}`}
        style={{
          width: `${options.column_width * options.columns}%`,
          margin: "0 auto",
          wordBreak: "keep-all",
          overflowWrap: "normal",
        }}
      >
        {arraySplitDataByRole?.map((items, index) => (
          <div
            key={index}
            className="roleName-gutter-column flex flex-col float-left"
            style={roleNameGutterColStyle}
          >
            {useOpticallyCenteredTwoColumnLayout ? (
              renderOpticallyCenteredGroups(items, index)
            ) : (
              renderPairGroups(items, (item, index) => (
                <table key={index} className="roleName_tr" style={tableGutterStyle}>
                  <tbody>
                    <RoleNamePair
                      {...getRoleNamePairProps(item)}
                    />
                  </tbody>
                </table>
              ))
            )}
          </div>
        ))}
      </div>
    )
  ) : (
    // Single Column (Gutter Align)

    <div
      className={`flex flex-row flex-wrap ${
        options?.columns === 1 ? "justify-center" : ""
      } 
      ${options?.text_alignment === "gutter" ? " justify-center " : ""}`}
      style={{
        width: `${options.column_width * options.columns}%`,
        margin: "0 auto",
        wordBreak: "keep-all",
        overflowWrap: "normal",
        justifyContent:
          options.text_alignment === "left"
            ? "flex-start"
            : options.text_alignment === "right"
            ? "flex-end"
            : "center",
      }}
    >
      {arraySplitData?.map((items, index) => (
        <div
          key={index}
          className={`flex flex-col flex-1 float-left`}
        >
          {renderPairGroups(
            items,
            (item, index) => (
            <div
              key={index}
              className={`flex flex-col`}
              style={{
                alignItems:
                  options.text_alignment === "left"
                    ? "flex-start"
                    : options.text_alignment === "right"
                    ? "flex-end"
                    : "center",
              }}
            >
              <span
                className="FontSIzeScale roleName_line"
                style={{
                  ...roleStyle,
                  textDecoration:
                    item.role == null ? "none" : fontStyle.role.textDecoration,
                  color:
                    item.role == null ? "transparent" : fontStyle.role.color,
                  lineHeight:
                    item.role == null ? "0" : fontStyle?.role?.lineHeight,
                  paddingLeft: "1px",
                  paddingRight: "1px",
                  whiteSpace: "normal",
                  wordBreak: "keep-all",
                  overflowWrap: "normal",
                  display: "inline-block",
                  maxWidth: "100%",
                  textAlign: stackedTextAlign,
                }}
              >
                {item.role == null ? "No Roll" : <SearchHit text={item.role} />}
              </span>
              <span
                className="FontSIzeScale roleName_line"
                style={{
                  ...nameStyle,
                  textDecoration: fontStyle.name.textDecoration,
                  color:
                    item.name == null ? "transparent" : fontStyle.name.color,
                  lineHeight:
                    item.name == null ? "0" : fontStyle?.name?.lineHeight,
                  paddingLeft: "1px",
                  paddingRight: "1px",
                  whiteSpace: "normal",
                  wordBreak: "keep-all",
                  overflowWrap: "normal",
                  display: "inline-block",
                  maxWidth: "100%",
                  textAlign: stackedTextAlign,
                }}
              >
                {item.name == null ? "No Roll" : <SearchHit text={item.name} />}
              </span>
            </div>
            ),
            options.name_on_left,
          )}
        </div>
      ))}
    </div>
  );
}

const areRoleNamePropsEqual = (previous, next) =>
  previous.items === next.items &&
  previous.options === next.options &&
  previous.settings === next.settings &&
  previous.isCard === next.isCard &&
  previous.fontStyle?.role === next.fontStyle?.role &&
  previous.fontStyle?.name === next.fontStyle?.name;

export default React.memo(RoleName2, areRoleNamePropsEqual);
